import React, { useState, useEffect, useMemo } from "react";
import "../css/search.css";
import "../css/index.css";
import HeaderSearch from "./header/HeaderSearch";
import Loader from "./Loader";
import RenderSearchResults from "./RenderSearchResults";
import FooterSticky from "./FooterSticky";
import { useAuth } from "../hooks/useAuth";
import { useStockCache } from "../hooks/useStockCache";
import { useDebounce } from "../hooks/useDebounce";
import { formatFileDate } from "../services/api";

export default function Search(props) {
  const [searchQuery, setSearchQuery] = useState("");
  const [scannerResult, setScannerResult] = useState("");

  // Hooks personnalisés
  const { shopId, token, isLoading: authLoading, error: authError } = useAuth();
  const { stockData, loading: cacheLoading, error: cacheError, searchProducts, cacheAge, isCached } = useStockCache(shopId, token);
  
  // Debounce de la recherche (300ms - plus rapide car recherche locale)
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Recherche dans les données en cache (seulement à partir de 2 caractères)
  const searchResults = useMemo(() => {
    if (!debouncedSearchQuery || !stockData || debouncedSearchQuery.trim().length < 2) {
      return [];
    }
    return searchProducts(debouncedSearchQuery);
  }, [debouncedSearchQuery, stockData, searchProducts]);

  // Calculer le message et la date
  const { message, fileDate } = useMemo(() => {
    if (!stockData) {
      return { message: '', fileDate: '' };
    }

    const fileDate = stockData.Header?.Horodatage 
      ? formatFileDate(stockData.Header.Horodatage.toString())
      : '';

    if (!debouncedSearchQuery || debouncedSearchQuery.trim().length < 2) {
      return { message: '', fileDate };
    }

    const singularOrPlural = searchResults.length > 1 ? 'produits' : 'produit';
    const resultMessage = searchResults.length === 0
      ? "Il n'y a pas de résultat pour les critères sélectionnés."
      : `${searchResults.length} ${singularOrPlural}`;

    return { message: resultMessage, fileDate };
  }, [stockData, debouncedSearchQuery, searchResults]);

  const handleInputChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
  };

  const onScanBarcode = (newScannerResult) => {
    setSearchQuery(newScannerResult);
    setScannerResult(newScannerResult);
    // La recherche sera déclenchée automatiquement par le useEffect avec debounce
  };

  // Afficher un loader pendant le chargement de l'authentification
  if (authLoading) {
    return (
      <div className="header-container">
        <Loader loading={true} />
      </div>
    );
  }

  // Afficher une erreur si l'authentification a échoué
  if (authError) {
    return (
      <div className="header-container">
        <div className="container">
          <p className="error-message">Erreur d'authentification: {authError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="header-container">
      <HeaderSearch
        handleInputChange={handleInputChange}
        message={message}
        scannerResult={scannerResult}
        onScanBarcode={onScanBarcode}
        image={props.image}
        query={searchQuery}
      />
      <div className="container">
        
        {isCached && cacheAge !== null && message && !cacheError && (
          <p className="cache-info" style={{ fontSize: '0.85rem', color: '#666', marginBottom: '10px' }}>
            ⏱️ {cacheAge} minute{cacheAge > 1 ? 's' : ''}  🎁 {message}
          </p>
        )}
        
        {cacheError && (
          <p className="error-message" role="alert">
            {cacheError}
          </p>
        )}
        
        {cacheLoading && !stockData && (
          <Loader loading={true} />
        )}
        
        {searchResults && searchResults.length > 0 && (
          <RenderSearchResults results={searchResults} />
        )}
      </div>
      {fileDate && <FooterSticky date={fileDate} />}
    </div>
  );
}